import type {
  ArtifactDto, ArtifactSummaryDto, BmcReadingDto, InnovationBatchDto, InnovationReadingDto, MacroReadingDto, MacroSuggestionDto, DnaOverviewDto, HorizonReadingDto, HorizonSuggestionDto, HorizonSuggestRequest, W5h2SuggestionDto, BmcSuggestionDto, BoardReadingDto, ValoresSuggestionDto, DesignSystemDto, DiferenciaisReadingDto, GapsReadingDto, TriadeTipDto, VisaoReadingDto, DnaSnapshotDto, MaterialDto, MaterialsDto, MeDto, ObjectiveAnalysisDto,
  ProjectDto, PublicationDto, PublishRequest,
  PublishSettingsDto, PublishSettingsInput, PulseDto, SetupAnswers, SetupBlockKey, SetupDto, SiteDto, StrategyContent,
  StrategyToolKey, StudioToolKey, ToolEntryDto, UsageDto,
} from '@ads/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { api } from './api';

export type ReadingTool = 'gaps' | 'diferenciais' | 'triade' | 'visao' | 'bmc' | 'inovacao' | 'macro' | 'horizontes';

export const keys = {
  me: ['me'] as const,
  project: (pid: string) => ['project', pid] as const,
  setup: (pid: string) => ['setup', pid] as const,
  tool: (pid: string, k: string) => ['tool', pid, k] as const,
  dna: (pid: string) => ['dna', pid] as const,
  pulse: (pid: string) => ['pulse', pid] as const,
  artifacts: (pid: string) => ['artifacts', pid] as const,
  artifact: (id: string) => ['artifact', id] as const,
  ds: (oid: string) => ['ds', oid] as const,
  uploads: (pid: string) => ['uploads', pid] as const,
  materials: (pid: string) => ['materials', pid] as const,
  objective: (pid: string) => ['objective', pid] as const,
  board: (pid: string) => ['board', pid] as const,
  evidence: (pid: string) => ['evidence', pid] as const,
  /** a leitura do agente de uma ferramenta do Strategy (o consolidado do Mapa de Gaps, a análise dos Diferenciais) */
  toolReadings: (pid: string) => ['tool-reading', pid] as const,
  toolReading: (pid: string, tool: ReadingTool) => ['tool-reading', pid, tool] as const,
  innovationBatch: (pid: string) => ['tool-reading', pid, 'inovacao-insights'] as const,
  macroSuggestions: (pid: string) => ['tool-reading', pid, 'macro-sugestoes'] as const,
  dnaOverview: (pid: string) => ['dna-overview', pid] as const,
  publish: (oid: string) => ['publish', oid] as const,
  sites: (oid: string) => ['sites', oid] as const,
  publications: (aid: string) => ['publications', aid] as const,
  publication: (id: string) => ['publication', id] as const,
};

/* ── contexto da sessão: quem, qual organização, qual projeto ─────── */
export interface Session { me: MeDto; projectId: string }
export const SessionContext = createContext<Session | null>(null);
export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('sem sessão');
  return s;
}

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api<MeDto>('/me'), retry: false, staleTime: 30_000 });

export function useProject() {
  const { projectId } = useSession();
  return useQuery({ queryKey: keys.project(projectId), queryFn: () => api<ProjectDto>(`/projects/${projectId}`) });
}

export function useSetup() {
  const { projectId } = useSession();
  return useQuery({ queryKey: keys.setup(projectId), queryFn: () => api<SetupDto>(`/projects/${projectId}/setup`), staleTime: Infinity });
}

export function useSaveSetup() {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { block: SetupBlockKey; data: SetupAnswers[SetupBlockKey] }) =>
      api<SetupDto>(`/projects/${projectId}/setup/${v.block}`, { method: 'PUT', body: v.data }),
    onSuccess: (dto, v) => {
      /* não sobrescreve o que o usuário está digitando: só a prontidão e as datas */
      qc.setQueryData<SetupDto>(keys.setup(projectId), old => (old ? { ...old, readiness: dto.readiness, updatedAt: dto.updatedAt } : dto));
      void qc.invalidateQueries({ queryKey: keys.project(projectId) });
      /* resposta nova na entrevista: a leitura do Board pode ter ficado para trás */
      if (v.block === 'board') void qc.invalidateQueries({ queryKey: keys.board(projectId) });
    },
  });
}

export function useTool<K extends StrategyToolKey>(key: K) {
  const { projectId } = useSession();
  return useQuery({
    queryKey: keys.tool(projectId, key),
    queryFn: () => api<ToolEntryDto<StrategyContent<K>>>(`/projects/${projectId}/tools/${key}`),
  });
}

export function useSaveTool<K extends StrategyToolKey>(key: K) {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: StrategyContent<K>) =>
      api<ToolEntryDto<StrategyContent<K>>>(`/projects/${projectId}/tools/${key}`, { method: 'PUT', body: content }),
    onMutate: async content => {
      await qc.cancelQueries({ queryKey: keys.tool(projectId, key) });
      const prev = qc.getQueryData<ToolEntryDto<StrategyContent<K>>>(keys.tool(projectId, key));
      if (prev) qc.setQueryData(keys.tool(projectId, key), { ...prev, content });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(keys.tool(projectId, key), ctx.prev); },
    onSuccess: dto => {
      qc.setQueryData(keys.tool(projectId, key), dto);
      void qc.invalidateQueries({ queryKey: keys.project(projectId) });
      void qc.invalidateQueries({ queryKey: keys.pulse(projectId) });
      void qc.invalidateQueries({ queryKey: keys.dnaOverview(projectId) });
      if (key === 'gaps' || key === 'diferenciais' || key === 'triade' || key === 'visao' || key === 'bmc' || key === 'inovacao' || key === 'macro' || key === 'horizontes') void qc.invalidateQueries({ queryKey: keys.toolReading(projectId, key) });
      if (key === 'inovacao') void qc.invalidateQueries({ queryKey: keys.innovationBatch(projectId) });
    },
  });
}

export function useDna() {
  const { projectId } = useSession();
  return useQuery({ queryKey: keys.dna(projectId), queryFn: () => api<DnaSnapshotDto>(`/projects/${projectId}/dna`), retry: false });
}

export function useConsolidate() {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<DnaSnapshotDto & { created: boolean }>(`/projects/${projectId}/dna/consolidate`, { method: 'POST' }),
    onSuccess: dto => {
      qc.setQueryData(keys.dna(projectId), dto);
      void qc.invalidateQueries({ queryKey: keys.dnaOverview(projectId) });
      void qc.invalidateQueries({ queryKey: keys.project(projectId) });
      void qc.invalidateQueries({ queryKey: keys.pulse(projectId) });
    },
  });
}

/** A estratégia inteira, ao vivo (a tela do DNA); atualiza sozinha enquanto está aberta. */
export function useDnaOverview() {
  const { projectId } = useSession();
  return useQuery({
    queryKey: keys.dnaOverview(projectId),
    queryFn: () => api<DnaOverviewDto>(`/projects/${projectId}/dna/overview`),
    refetchInterval: 30_000,
  });
}

export function usePulse() {
  const { projectId } = useSession();
  /* a semana corrente se mede a cada abertura; enquanto a tela está aberta, de minuto em minuto */
  return useQuery({ queryKey: keys.pulse(projectId), queryFn: () => api<PulseDto>(`/projects/${projectId}/pulse`), refetchInterval: 60_000 });
}

export function useArtifacts() {
  const { projectId } = useSession();
  return useQuery({ queryKey: keys.artifacts(projectId), queryFn: () => api<ArtifactSummaryDto[]>(`/projects/${projectId}/artifacts`) });
}

export function useArtifact(id: string | undefined) {
  return useQuery({
    queryKey: keys.artifact(id ?? ''), enabled: !!id,
    queryFn: () => api<ArtifactDto>(`/artifacts/${id}`),
    refetchInterval: q => (q.state.data?.status === 'generating' ? 2500 : false),
  });
}

export function useDesignSystem() {
  const { me } = useSession();
  return useQuery({ queryKey: keys.ds(me.org.id), queryFn: () => api<DesignSystemDto>(`/orgs/${me.org.id}/design-system`), retry: false, staleTime: 60_000 });
}

/* ── materiais do setup: os arquivos e o que o ADS leu neles ─────── */

/** Enquanto houver arquivo na fila de leitura, a lista se atualiza sozinha. */
export function useMaterials() {
  const { projectId } = useSession();
  return useQuery({
    queryKey: keys.materials(projectId),
    queryFn: () => api<MaterialsDto>(`/projects/${projectId}/materials`),
    refetchInterval: q => (q.state.data?.busy ? 1500 : false),
  });
}

/* alguns navegadores não informam o tipo de .md, .csv ou .zip */
const TYPE_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf', doc: 'application/msword', ppt: 'application/vnd.ms-powerpoint', xls: 'application/vnd.ms-excel',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain', md: 'text/markdown', csv: 'text/csv', zip: 'application/zip',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml',
};
const typeOf = (f: File) => {
  const ext = /\.([a-z0-9]+)$/i.exec(f.name)?.[1]?.toLowerCase() ?? '';
  return f.type && f.type !== 'application/octet-stream' ? f.type : TYPE_BY_EXT[ext] ?? 'application/octet-stream';
};

/** Upload em três passos: pede o destino, envia o arquivo, confirma. Depois, o ADS lê. */
export function useUpload() {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, kind, ref }: { file: File; kind: 'document' | 'logo'; ref?: string }) => {
      const contentType = typeOf(file);
      const t = await api<{ id: string; upload: { url: string; method: 'PUT'; headers: Record<string, string> } }>(
        `/projects/${projectId}/uploads`, { method: 'POST', body: { filename: file.name, contentType, size: file.size, kind, ...(ref ? { purpose: 'evidence', ref } : {}) } });
      const put = await fetch(t.upload.url, { method: 'PUT', headers: t.upload.headers, body: file, credentials: t.upload.url.startsWith('/') ? 'same-origin' : 'omit' });
      if (!put.ok) throw new Error('O envio do arquivo falhou.');
      return api<MaterialDto>(`/uploads/${t.id}/complete`, { method: 'POST' });
    },
    onSuccess: m => {
      if (m.purpose === 'evidence') {
        qc.setQueryData<MaterialDto[]>(keys.evidence(projectId), old => (old ? [...old.filter(i => i.id !== m.id), m] : old));
        void qc.invalidateQueries({ queryKey: keys.evidence(projectId) });
        void qc.invalidateQueries({ queryKey: keys.toolReadings(projectId) });
        return;
      }
      qc.setQueryData<MaterialsDto>(keys.materials(projectId), old => (old ? { ...old, items: [...old.items.filter(i => i.id !== m.id), m], busy: true } : old));
      void qc.invalidateQueries({ queryKey: keys.materials(projectId) });
    },
  });
}

export function useMaterialAction() {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'delete' | 'read' }): Promise<unknown> =>
      action === 'delete' ? api<void>(`/uploads/${id}`, { method: 'DELETE' }) : api<MaterialDto>(`/uploads/${id}/read`, { method: 'POST' }),
    onSuccess: (_, v) => {
      qc.setQueryData<MaterialDto[]>(keys.evidence(projectId), old => old && (v.action === 'delete'
        ? old.filter(i => i.id !== v.id) : old.map(i => (i.id === v.id ? { ...i, reading: 'pending', readNote: null } : i))));
      void qc.invalidateQueries({ queryKey: keys.evidence(projectId) });
      void qc.invalidateQueries({ queryKey: keys.toolReadings(projectId) });
      qc.setQueryData<MaterialsDto>(keys.materials(projectId), old => old && ({
        ...old, busy: true,
        items: v.action === 'delete' ? old.items.filter(i => i.id !== v.id) : old.items.map(i => (i.id === v.id ? { ...i, reading: 'pending', readNote: null } : i)),
      }));
      void qc.invalidateQueries({ queryKey: keys.materials(projectId) });
    },
  });
}

/* ── objetivo: interpretação e força ──────────────────────────────── */

export function useObjective() {
  const { projectId } = useSession();
  return useQuery({ queryKey: keys.objective(projectId), queryFn: () => api<ObjectiveAnalysisDto | null>(`/projects/${projectId}/objective`), staleTime: 60_000 });
}

/* ── Strategy: evidências e as leituras do agente ─────────────────── */

/** Todas as evidências do projeto; enquanto alguma estiver sendo lida, atualiza sozinho. */
export function useEvidence() {
  const { projectId } = useSession();
  return useQuery({
    queryKey: keys.evidence(projectId),
    queryFn: () => api<MaterialDto[]>(`/projects/${projectId}/evidence`),
    refetchInterval: q => (q.state.data?.some(i => i.reading === 'pending' || i.reading === 'reading') ? 1500 : false),
  });
}

interface ReadingDtos { gaps: GapsReadingDto; diferenciais: DiferenciaisReadingDto; triade: TriadeTipDto; visao: VisaoReadingDto; bmc: BmcReadingDto; inovacao: InnovationReadingDto; macro: MacroReadingDto; horizontes: HorizonReadingDto }

/** A leitura do agente sobre uma ferramenta; enquanto estiver agendada ou atrasada, atualiza sozinha. */
export function useToolReading<T extends ReadingTool>(tool: T) {
  const { projectId } = useSession();
  return useQuery({
    queryKey: keys.toolReading(projectId, tool),
    queryFn: () => api<ReadingDtos[T]>(`/projects/${projectId}/tools/${tool}/reading`),
    refetchInterval: q => (q.state.data?.pending || q.state.data?.stale ? 2000 : false),
  });
}
export const useGapsReading = () => useToolReading('gaps');

/** A última rodada de insights de inovação; enquanto houver uma agendada, atualiza sozinha. */
export function useInnovationBatch() {
  const { projectId } = useSession();
  return useQuery({
    queryKey: keys.innovationBatch(projectId),
    queryFn: () => api<InnovationBatchDto>(`/projects/${projectId}/tools/inovacao/insights`),
    refetchInterval: q => (q.state.data?.pending || q.state.data?.stale ? 2000 : false),
  });
}
/** Uma rodada nova de insights, agora. */
export function useGenerateInsights() {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<InnovationBatchDto>(`/projects/${projectId}/tools/inovacao/insights`, { method: 'POST' }),
    onSuccess: dto => qc.setQueryData(keys.innovationBatch(projectId), dto),
  });
}

/** O repertório de sugestões do Macro Cenário, por célula; enquanto estiver sendo gerado, atualiza sozinho. */
export function useMacroSuggestions() {
  const { projectId } = useSession();
  return useQuery({
    queryKey: keys.macroSuggestions(projectId),
    queryFn: () => api<MacroSuggestionDto>(`/projects/${projectId}/tools/macro/suggestions`),
    refetchInterval: q => (q.state.data?.pending || q.state.data?.stale ? 2000 : false),
  });
}
/** Outro repertório, agora (evita o que já está na matriz). */
export function useRefreshMacroSuggestions() {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<MacroSuggestionDto>(`/projects/${projectId}/tools/macro/suggestions`, { method: 'POST' }),
    onSuccess: dto => qc.setQueryData(keys.macroSuggestions(projectId), dto),
  });
}

/** Uma iniciativa sugerida pela IA para um horizonte (ou a descrição de uma já nomeada). Nada é gravado aqui. */
export function useHorizonSuggest() {
  const { projectId } = useSession();
  return useMutation({
    mutationFn: (req: Partial<HorizonSuggestRequest> & Pick<HorizonSuggestRequest, 'horizon'>) =>
      api<HorizonSuggestionDto>(`/projects/${projectId}/tools/horizontes/suggest`, { method: 'POST', body: req }),
  });
}

/** As ações que a IA propõe a partir do snapshot do Strategy (prioridade e "o quê"). Nada é gravado aqui. */
export function useW5h2Suggest() {
  const { projectId } = useSession();
  return useMutation({
    mutationFn: (avoid: string[]) => api<W5h2SuggestionDto>(`/projects/${projectId}/tools/w5h2/suggest`, { method: 'POST', body: { avoid } }),
  });
}

/** A sugestão de detalhamento dos valores (a única coisa que a IA faz em Valores). Nada é gravado aqui. */
export function useValoresSuggest() {
  const { projectId } = useSession();
  return useMutation({
    mutationFn: (ids: string[] = []) => api<ValoresSuggestionDto>(`/projects/${projectId}/tools/valores/suggest`, { method: 'POST', body: { ids } }),
  });
}

/** Sugestões da IA para o Business Model Canvas: a proposta de valor, ou itens para os blocos. Nada é gravado aqui. */
export function useBmcSuggest() {
  const { projectId } = useSession();
  return useMutation({
    mutationFn: (req: { mode: 'proposta' | 'modulos'; blocks?: string[] }) =>
      api<BmcSuggestionDto>(`/projects/${projectId}/tools/bmc/suggest`, { method: 'POST', body: { blocks: [], ...req } }),
  });
}

export async function uploadText(id: string) {
  return api<{ id: string; filename: string; reading: string; sections: Array<{ label: string | null; text: string }>; truncated: boolean }>(`/uploads/${id}/text`);
}

/* ── Board: a leitura das entrevistas ─────────────────────────────── */

export function useBoardReading() {
  const { projectId } = useSession();
  return useQuery({ queryKey: keys.board(projectId), queryFn: () => api<BoardReadingDto | null>(`/projects/${projectId}/board`) });
}

export function useAnalyzeBoard() {
  const { projectId } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<BoardReadingDto>(`/projects/${projectId}/board/analyze`, { method: 'POST' }),
    onSuccess: dto => qc.setQueryData(keys.board(projectId), dto),
  });
}

/** Quem chama decide se a resposta ainda vale (a frase pode ter mudado no caminho). */
export function useAnalyzeObjective() {
  const { projectId } = useSession();
  return useMutation({
    mutationFn: (v: { goal: string; force?: boolean }) =>
      api<ObjectiveAnalysisDto>(`/projects/${projectId}/objective/analyze`, { method: 'POST', body: v }),
  });
}

/* ── publicação ───────────────────────────────────────────────────── */

export function usePublishSettings() {
  const { me } = useSession();
  return useQuery({ queryKey: keys.publish(me.org.id), queryFn: () => api<PublishSettingsDto>(`/orgs/${me.org.id}/publish`), staleTime: 30_000 });
}

export function useSavePublishSettings() {
  const { me } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: PublishSettingsInput) => api<PublishSettingsDto>(`/orgs/${me.org.id}/publish`, { method: 'PUT', body: v }),
    onSuccess: dto => qc.setQueryData(keys.publish(me.org.id), dto),
  });
}

export function useSites() {
  const { me } = useSession();
  return useQuery({ queryKey: keys.sites(me.org.id), queryFn: () => api<SiteDto[]>(`/orgs/${me.org.id}/sites`) });
}

export function usePublications(artifactId: string | null | undefined) {
  return useQuery({
    queryKey: keys.publications(artifactId ?? ''), enabled: !!artifactId,
    queryFn: () => api<PublicationDto[]>(`/artifacts/${artifactId}/publications`),
  });
}

/** Acompanha uma publicação passo a passo, até terminar. */
export function usePublication(id: string | null) {
  const { me } = useSession();
  const qc = useQueryClient();
  return useQuery({
    queryKey: keys.publication(id ?? ''), enabled: !!id,
    queryFn: async () => {
      const p = await api<PublicationDto>(`/publications/${id}`);
      if (p.status !== 'running') {
        void qc.invalidateQueries({ queryKey: keys.sites(me.org.id) });
        void qc.invalidateQueries({ queryKey: keys.publications(p.artifactId) });
      }
      return p;
    },
    refetchInterval: q => (q.state.data && q.state.data.status !== 'running' ? false : 700),
  });
}

export function usePublish(artifactId: string) {
  return useMutation({
    mutationFn: (req: PublishRequest) => api<PublicationDto>(`/artifacts/${artifactId}/publish`, { method: 'POST', body: req }),
  });
}

export function useUsage() {
  const { me } = useSession();
  return useQuery({ queryKey: ['usage', me.org.id], queryFn: () => api<UsageDto>(`/orgs/${me.org.id}/usage`), staleTime: 30_000 });
}

export type { StudioToolKey };
